import { BS_STRUCTURE } from './bs-category-config.js';
import { CalcInputError, fromCents, toCents } from './calc/money.js';

const gridLines = new Set<string>(BS_STRUCTURE.flatMap(category => category.type === 'input'
  ? [...(category.subcategories?.flatMap(sub => sub.items) ?? []), ...(category.items ?? [])].map(line => line.id)
  : []));

/** Figures outside this grid cannot establish that the displayed sheet balances. */
export function hasUnrepresentedBalanceSheetLines(items: readonly { lineItem: string; amount: string | number }[]): boolean {
  return items.some(item => !gridLines.has(item.lineItem) && Number(item.amount) !== 0);
}

/** Preserve editable source figures when the bounded read-only totals cannot be computed. */
export function tryBalanceSheetTotals(...args: Parameters<typeof balanceSheetTotals>): Record<string, number> | null {
  try {
    return balanceSheetTotals(...args);
  } catch (error) {
    if (error instanceof CalcInputError) return null;
    throw error;
  }
}

/** Read-only grid/validation categories; not an input to ratios or stored calculations. */
export const BS_CUSTOM_CATEGORIES: Record<string, string> = {
  current_assets: 'current_asset',
  non_current_assets: 'non_current_asset',
  current_liabilities: 'current_liability',
  non_current_liabilities: 'non_current_liability',
  equity: 'equity',
};

export function balanceSheetTotals(
  items: readonly { lineItem: string; amount: string | number }[],
  customItems: readonly { category: string; amount: string | number }[] = [],
): Record<string, number> {
  const amounts = new Map(items.map(item => [item.lineItem, item.amount]));
  const cents: Record<string, number> = {};
  for (const category of BS_STRUCTURE) {
    if (category.type !== 'input') continue;
    const lines = [...(category.subcategories?.flatMap(sub => sub.items) ?? []), ...(category.items ?? [])];
    cents[`total_${category.key}`] = lines.reduce((sum, line) =>
      sum + toCents(amounts.get(line.id) ?? 0) * (line.sign === '-' ? -1 : 1), 0)
      + customItems.filter(item => item.category === BS_CUSTOM_CATEGORIES[category.key])
        .reduce((sum, item) => sum + toCents(item.amount), 0);
  }
  cents.total_assets = cents.total_current_assets + cents.total_non_current_assets;
  cents.total_liabilities = cents.total_current_liabilities + cents.total_non_current_liabilities;
  cents.balance_check = cents.total_assets - cents.total_liabilities - cents.total_equity;
  return Object.fromEntries(Object.entries(cents).map(([key, value]) => [key, fromCents(value)]));
}
